import * as z from 'zod';

export const CreateDeliveryProfileRequest = z.object({
  deliveryProfile: z
    .object({
      _id: z
        .string()
        .describe('Delivery profile ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z.string().describe('Delivery profile name.').min(1).max(256),
      default: z
        .boolean()
        .describe(
          "Whether this is the default delivery profile.\n\n> **Notes:**\n>\n> - The first delivery profile is automatically created and marked as default when the Wix Stores, Wix Bookings, Wix Events, or Wix Restaurants business solutions are installed on a site.\n> - Default status can't be changed or transferred to another profile.\n> - The default profile can't be deleted because every site requires one for core delivery functionality."
        )
        .optional()
        .nullable(),
      deliveryRegions: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Delivery region ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe(
                'Delivery region name. For example, `"Domestic"` or `"International"`.'
              )
              .min(1)
              .max(256),
            active: z
              .boolean()
              .describe(
                'Whether this delivery region is active and available during checkout.\n\nDefault: `true`'
              )
              .optional()
              .nullable(),
            deliveryCarriers: z
              .array(
                z.object({
                  appId: z
                    .string()
                    .describe(
                      'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  backupRate: z
                    .object({
                      title: z
                        .string()
                        .describe(
                          'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
                        )
                        .min(1)
                        .max(256)
                        .optional()
                        .nullable(),
                      amount: z
                        .string()
                        .describe('Backup rate amount.')
                        .optional()
                        .nullable(),
                      active: z
                        .boolean()
                        .describe(
                          'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
                        )
                        .optional(),
                    })
                    .describe(
                      "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
                    )
                    .optional(),
                  additionalCharges: z
                    .array(
                      z.object({
                        description: z
                          .string()
                          .describe(
                            'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                          )
                          .max(250)
                          .optional()
                          .nullable(),
                        amount: z
                          .string()
                          .describe(
                            'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                          )
                          .max(16)
                          .optional(),
                      })
                    )
                    .max(10)
                    .optional(),
                })
              )
              .min(0)
              .max(25)
              .optional(),
            destinations: z
              .array(
                z.object({
                  countryCode: z
                    .string()
                    .describe(
                      '2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.'
                    ),
                  subdivisions: z.array(z.string()).min(0).max(100).optional(),
                })
              )
              .min(0)
              .max(250)
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the delivery region was created.')
              .optional()
              .nullable(),
          })
        )
        .min(0)
        .max(100)
        .optional(),
      createdBy: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              appId: z.never().optional(),
              userId: z.never().optional(),
            }),
            z.object({
              userId: z.never().optional(),
              appId: z
                .string()
                .describe(
                  'App ID, when the delivery profile was created by an external application or Wix service.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              appId: z.never().optional(),
              userId: z
                .string()
                .describe(
                  'Wix user ID, when the delivery profile was created by a Wix user using the dashboard or an API call.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe(
          'Information about who created the delivery profile.\n\nCurrently only for use with Wix Restaurants.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the delivery profile is updated. To prevent conflicting changes, the current revision must be passed when updating the delivery profile. Ignored when creating a delivery profile.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the delivery profile was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the delivery profile was last updated.')
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the delivery profile object. [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
    })
    .describe('Delivery profile to create.'),
});
export const CreateDeliveryProfileResponse = z.object({
  _id: z
    .string()
    .describe('Delivery profile ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Delivery profile name.')
    .min(1)
    .max(256)
    .optional()
    .nullable(),
  default: z
    .boolean()
    .describe(
      "Whether this is the default delivery profile.\n\n> **Notes:**\n>\n> - The first delivery profile is automatically created and marked as default when the Wix Stores, Wix Bookings, Wix Events, or Wix Restaurants business solutions are installed on a site.\n> - Default status can't be changed or transferred to another profile.\n> - The default profile can't be deleted because every site requires one for core delivery functionality."
    )
    .optional()
    .nullable(),
  deliveryRegions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Delivery region ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe(
            'Delivery region name. For example, `"Domestic"` or `"International"`.'
          )
          .min(1)
          .max(256)
          .optional()
          .nullable(),
        active: z
          .boolean()
          .describe(
            'Whether this delivery region is active and available during checkout.\n\nDefault: `true`'
          )
          .optional()
          .nullable(),
        deliveryCarriers: z
          .array(
            z.object({
              appId: z
                .string()
                .describe(
                  'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              backupRate: z
                .object({
                  title: z
                    .string()
                    .describe(
                      'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
                    )
                    .min(1)
                    .max(256)
                    .optional()
                    .nullable(),
                  amount: z
                    .string()
                    .describe('Backup rate amount.')
                    .optional()
                    .nullable(),
                  active: z
                    .boolean()
                    .describe(
                      'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
                    )
                    .optional(),
                })
                .describe(
                  "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
                )
                .optional(),
              additionalCharges: z
                .array(
                  z.object({
                    description: z
                      .string()
                      .describe(
                        'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                      )
                      .max(250)
                      .optional()
                      .nullable(),
                    amount: z
                      .string()
                      .describe(
                        'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                      )
                      .max(16)
                      .optional(),
                  })
                )
                .max(10)
                .optional(),
            })
          )
          .min(0)
          .max(25)
          .optional(),
        destinations: z
          .array(
            z.object({
              countryCode: z
                .string()
                .describe(
                  '2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.'
                )
                .optional(),
              subdivisions: z.array(z.string()).min(0).max(100).optional(),
            })
          )
          .min(0)
          .max(250)
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time the delivery region was created.')
          .optional()
          .nullable(),
      })
    )
    .min(0)
    .max(100)
    .optional(),
  createdBy: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({ appId: z.never().optional(), userId: z.never().optional() }),
        z.object({
          userId: z.never().optional(),
          appId: z
            .string()
            .describe(
              'App ID, when the delivery profile was created by an external application or Wix service.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          appId: z.never().optional(),
          userId: z
            .string()
            .describe(
              'Wix user ID, when the delivery profile was created by a Wix user using the dashboard or an API call.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
      ])
    )
    .describe(
      'Information about who created the delivery profile.\n\nCurrently only for use with Wix Restaurants.'
    )
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the delivery profile is updated. To prevent conflicting changes, the current revision must be passed when updating the delivery profile. Ignored when creating a delivery profile.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the delivery profile was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the delivery profile was last updated.')
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the delivery profile object. [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
});
export const GetDeliveryProfileRequest = z.object({
  deliveryProfileId: z
    .string()
    .describe('Delivery profile ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetDeliveryProfileResponse = z.object({
  _id: z
    .string()
    .describe('Delivery profile ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Delivery profile name.')
    .min(1)
    .max(256)
    .optional()
    .nullable(),
  default: z
    .boolean()
    .describe(
      "Whether this is the default delivery profile.\n\n> **Notes:**\n>\n> - The first delivery profile is automatically created and marked as default when the Wix Stores, Wix Bookings, Wix Events, or Wix Restaurants business solutions are installed on a site.\n> - Default status can't be changed or transferred to another profile.\n> - The default profile can't be deleted because every site requires one for core delivery functionality."
    )
    .optional()
    .nullable(),
  deliveryRegions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Delivery region ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe(
            'Delivery region name. For example, `"Domestic"` or `"International"`.'
          )
          .min(1)
          .max(256)
          .optional()
          .nullable(),
        active: z
          .boolean()
          .describe(
            'Whether this delivery region is active and available during checkout.\n\nDefault: `true`'
          )
          .optional()
          .nullable(),
        deliveryCarriers: z
          .array(
            z.object({
              appId: z
                .string()
                .describe(
                  'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              backupRate: z
                .object({
                  title: z
                    .string()
                    .describe(
                      'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
                    )
                    .min(1)
                    .max(256)
                    .optional()
                    .nullable(),
                  amount: z
                    .string()
                    .describe('Backup rate amount.')
                    .optional()
                    .nullable(),
                  active: z
                    .boolean()
                    .describe(
                      'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
                    )
                    .optional(),
                })
                .describe(
                  "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
                )
                .optional(),
              additionalCharges: z
                .array(
                  z.object({
                    description: z
                      .string()
                      .describe(
                        'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                      )
                      .max(250)
                      .optional()
                      .nullable(),
                    amount: z
                      .string()
                      .describe(
                        'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                      )
                      .max(16)
                      .optional(),
                  })
                )
                .max(10)
                .optional(),
            })
          )
          .min(0)
          .max(25)
          .optional(),
        destinations: z
          .array(
            z.object({
              countryCode: z
                .string()
                .describe(
                  '2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.'
                )
                .optional(),
              subdivisions: z.array(z.string()).min(0).max(100).optional(),
            })
          )
          .min(0)
          .max(250)
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time the delivery region was created.')
          .optional()
          .nullable(),
      })
    )
    .min(0)
    .max(100)
    .optional(),
  createdBy: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({ appId: z.never().optional(), userId: z.never().optional() }),
        z.object({
          userId: z.never().optional(),
          appId: z
            .string()
            .describe(
              'App ID, when the delivery profile was created by an external application or Wix service.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          appId: z.never().optional(),
          userId: z
            .string()
            .describe(
              'Wix user ID, when the delivery profile was created by a Wix user using the dashboard or an API call.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
      ])
    )
    .describe(
      'Information about who created the delivery profile.\n\nCurrently only for use with Wix Restaurants.'
    )
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the delivery profile is updated. To prevent conflicting changes, the current revision must be passed when updating the delivery profile. Ignored when creating a delivery profile.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the delivery profile was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the delivery profile was last updated.')
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the delivery profile object. [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
});
export const UpdateDeliveryProfileRequest = z.object({
  _id: z
    .string()
    .describe('Delivery profile ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  deliveryProfile: z
    .object({
      _id: z
        .string()
        .describe('Delivery profile ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Delivery profile name.')
        .min(1)
        .max(256)
        .optional()
        .nullable(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default delivery profile.\n\n> **Notes:**\n>\n> - The first delivery profile is automatically created and marked as default when the Wix Stores, Wix Bookings, Wix Events, or Wix Restaurants business solutions are installed on a site.\n> - Default status can't be changed or transferred to another profile.\n> - The default profile can't be deleted because every site requires one for core delivery functionality."
        )
        .optional()
        .nullable(),
      deliveryRegions: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Delivery region ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe(
                'Delivery region name. For example, `"Domestic"` or `"International"`.'
              )
              .min(1)
              .max(256)
              .optional()
              .nullable(),
            active: z
              .boolean()
              .describe(
                'Whether this delivery region is active and available during checkout.\n\nDefault: `true`'
              )
              .optional()
              .nullable(),
            deliveryCarriers: z
              .array(
                z.object({
                  appId: z
                    .string()
                    .describe(
                      'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  backupRate: z
                    .object({
                      title: z
                        .string()
                        .describe(
                          'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
                        )
                        .min(1)
                        .max(256)
                        .optional()
                        .nullable(),
                      amount: z
                        .string()
                        .describe('Backup rate amount.')
                        .optional()
                        .nullable(),
                      active: z
                        .boolean()
                        .describe(
                          'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
                        )
                        .optional(),
                    })
                    .describe(
                      "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
                    )
                    .optional(),
                  additionalCharges: z
                    .array(
                      z.object({
                        description: z
                          .string()
                          .describe(
                            'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                          )
                          .max(250)
                          .optional()
                          .nullable(),
                        amount: z
                          .string()
                          .describe(
                            'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                          )
                          .max(16)
                          .optional(),
                      })
                    )
                    .max(10)
                    .optional(),
                })
              )
              .min(0)
              .max(25)
              .optional(),
            destinations: z
              .array(
                z.object({
                  countryCode: z
                    .string()
                    .describe(
                      '2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.'
                    )
                    .optional(),
                  subdivisions: z.array(z.string()).min(0).max(100).optional(),
                })
              )
              .min(0)
              .max(250)
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the delivery region was created.')
              .optional()
              .nullable(),
          })
        )
        .min(0)
        .max(100)
        .optional(),
      createdBy: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              appId: z.never().optional(),
              userId: z.never().optional(),
            }),
            z.object({
              userId: z.never().optional(),
              appId: z
                .string()
                .describe(
                  'App ID, when the delivery profile was created by an external application or Wix service.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              appId: z.never().optional(),
              userId: z
                .string()
                .describe(
                  'Wix user ID, when the delivery profile was created by a Wix user using the dashboard or an API call.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe(
          'Information about who created the delivery profile.\n\nCurrently only for use with Wix Restaurants.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the delivery profile is updated. To prevent conflicting changes, the current revision must be passed when updating the delivery profile. Ignored when creating a delivery profile.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the delivery profile was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the delivery profile was last updated.')
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the delivery profile object. [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
    })
    .describe('Delivery profile to update.'),
});
export const UpdateDeliveryProfileResponse = z.object({
  _id: z
    .string()
    .describe('Delivery profile ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Delivery profile name.')
    .min(1)
    .max(256)
    .optional()
    .nullable(),
  default: z
    .boolean()
    .describe(
      "Whether this is the default delivery profile.\n\n> **Notes:**\n>\n> - The first delivery profile is automatically created and marked as default when the Wix Stores, Wix Bookings, Wix Events, or Wix Restaurants business solutions are installed on a site.\n> - Default status can't be changed or transferred to another profile.\n> - The default profile can't be deleted because every site requires one for core delivery functionality."
    )
    .optional()
    .nullable(),
  deliveryRegions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Delivery region ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe(
            'Delivery region name. For example, `"Domestic"` or `"International"`.'
          )
          .min(1)
          .max(256)
          .optional()
          .nullable(),
        active: z
          .boolean()
          .describe(
            'Whether this delivery region is active and available during checkout.\n\nDefault: `true`'
          )
          .optional()
          .nullable(),
        deliveryCarriers: z
          .array(
            z.object({
              appId: z
                .string()
                .describe(
                  'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              backupRate: z
                .object({
                  title: z
                    .string()
                    .describe(
                      'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
                    )
                    .min(1)
                    .max(256)
                    .optional()
                    .nullable(),
                  amount: z
                    .string()
                    .describe('Backup rate amount.')
                    .optional()
                    .nullable(),
                  active: z
                    .boolean()
                    .describe(
                      'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
                    )
                    .optional(),
                })
                .describe(
                  "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
                )
                .optional(),
              additionalCharges: z
                .array(
                  z.object({
                    description: z
                      .string()
                      .describe(
                        'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                      )
                      .max(250)
                      .optional()
                      .nullable(),
                    amount: z
                      .string()
                      .describe(
                        'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                      )
                      .max(16)
                      .optional(),
                  })
                )
                .max(10)
                .optional(),
            })
          )
          .min(0)
          .max(25)
          .optional(),
        destinations: z
          .array(
            z.object({
              countryCode: z
                .string()
                .describe(
                  '2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.'
                )
                .optional(),
              subdivisions: z.array(z.string()).min(0).max(100).optional(),
            })
          )
          .min(0)
          .max(250)
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time the delivery region was created.')
          .optional()
          .nullable(),
      })
    )
    .min(0)
    .max(100)
    .optional(),
  createdBy: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({ appId: z.never().optional(), userId: z.never().optional() }),
        z.object({
          userId: z.never().optional(),
          appId: z
            .string()
            .describe(
              'App ID, when the delivery profile was created by an external application or Wix service.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          appId: z.never().optional(),
          userId: z
            .string()
            .describe(
              'Wix user ID, when the delivery profile was created by a Wix user using the dashboard or an API call.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
      ])
    )
    .describe(
      'Information about who created the delivery profile.\n\nCurrently only for use with Wix Restaurants.'
    )
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the delivery profile is updated. To prevent conflicting changes, the current revision must be passed when updating the delivery profile. Ignored when creating a delivery profile.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the delivery profile was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the delivery profile was last updated.')
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the delivery profile object. [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
});
export const DeleteDeliveryProfileRequest = z.object({
  deliveryProfileId: z
    .string()
    .describe('Delivery profile ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteDeliveryProfileResponse = z.object({});
export const QueryDeliveryProfilesRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('Query options.'),
});
export const QueryDeliveryProfilesResponse = z.object({
  deliveryProfiles: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Delivery profile ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Delivery profile name.')
          .min(1)
          .max(256)
          .optional()
          .nullable(),
        default: z
          .boolean()
          .describe(
            "Whether this is the default delivery profile.\n\n> **Notes:**\n>\n> - The first delivery profile is automatically created and marked as default when the Wix Stores, Wix Bookings, Wix Events, or Wix Restaurants business solutions are installed on a site.\n> - Default status can't be changed or transferred to another profile.\n> - The default profile can't be deleted because every site requires one for core delivery functionality."
          )
          .optional()
          .nullable(),
        deliveryRegions: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Delivery region ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              name: z
                .string()
                .describe(
                  'Delivery region name. For example, `"Domestic"` or `"International"`.'
                )
                .min(1)
                .max(256)
                .optional()
                .nullable(),
              active: z
                .boolean()
                .describe(
                  'Whether this delivery region is active and available during checkout.\n\nDefault: `true`'
                )
                .optional()
                .nullable(),
              deliveryCarriers: z
                .array(
                  z.object({
                    appId: z
                      .string()
                      .describe(
                        'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    backupRate: z
                      .object({
                        title: z
                          .string()
                          .describe(
                            'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
                          )
                          .min(1)
                          .max(256)
                          .optional()
                          .nullable(),
                        amount: z
                          .string()
                          .describe('Backup rate amount.')
                          .optional()
                          .nullable(),
                        active: z
                          .boolean()
                          .describe(
                            'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
                          )
                          .optional(),
                      })
                      .describe(
                        "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
                      )
                      .optional(),
                    additionalCharges: z
                      .array(
                        z.object({
                          description: z
                            .string()
                            .describe(
                              'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                            )
                            .max(250)
                            .optional()
                            .nullable(),
                          amount: z
                            .string()
                            .describe(
                              'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                            )
                            .max(16)
                            .optional(),
                        })
                      )
                      .max(10)
                      .optional(),
                  })
                )
                .min(0)
                .max(25)
                .optional(),
              destinations: z
                .array(
                  z.object({
                    countryCode: z
                      .string()
                      .describe(
                        '2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.'
                      )
                      .optional(),
                    subdivisions: z
                      .array(z.string())
                      .min(0)
                      .max(100)
                      .optional(),
                  })
                )
                .min(0)
                .max(250)
                .optional(),
              _createdDate: z
                .date()
                .describe('Date and time the delivery region was created.')
                .optional()
                .nullable(),
            })
          )
          .min(0)
          .max(100)
          .optional(),
        createdBy: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                appId: z.never().optional(),
                userId: z.never().optional(),
              }),
              z.object({
                userId: z.never().optional(),
                appId: z
                  .string()
                  .describe(
                    'App ID, when the delivery profile was created by an external application or Wix service.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                appId: z.never().optional(),
                userId: z
                  .string()
                  .describe(
                    'Wix user ID, when the delivery profile was created by a Wix user using the dashboard or an API call.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe(
            'Information about who created the delivery profile.\n\nCurrently only for use with Wix Restaurants.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the delivery profile is updated. To prevent conflicting changes, the current revision must be passed when updating the delivery profile. Ignored when creating a delivery profile.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the delivery profile was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the delivery profile was last updated.')
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the delivery profile object. [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
      })
    )
    .min(0)
    .max(100)
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const AddDeliveryRegionRequest = z.object({
  deliveryProfileId: z
    .string()
    .describe('Delivery profile ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  deliveryRegion: z
    .object({
      _id: z
        .string()
        .describe('Delivery region ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe(
          'Delivery region name. For example, `"Domestic"` or `"International"`.'
        )
        .min(1)
        .max(256),
      active: z
        .boolean()
        .describe(
          'Whether this delivery region is active and available during checkout.\n\nDefault: `true`'
        )
        .optional()
        .nullable(),
      deliveryCarriers: z
        .array(
          z.object({
            appId: z
              .string()
              .describe(
                'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            backupRate: z
              .object({
                title: z
                  .string()
                  .describe(
                    'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
                  )
                  .min(1)
                  .max(256)
                  .optional()
                  .nullable(),
                amount: z
                  .string()
                  .describe('Backup rate amount.')
                  .optional()
                  .nullable(),
                active: z
                  .boolean()
                  .describe(
                    'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
                  )
                  .optional(),
              })
              .describe(
                "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
              )
              .optional(),
            additionalCharges: z
              .array(
                z.object({
                  description: z
                    .string()
                    .describe(
                      'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                    )
                    .max(250)
                    .optional()
                    .nullable(),
                  amount: z
                    .string()
                    .describe(
                      'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                    )
                    .max(16)
                    .optional(),
                })
              )
              .max(10)
              .optional(),
          })
        )
        .min(0)
        .max(25)
        .optional(),
      destinations: z
        .array(
          z.object({
            countryCode: z
              .string()
              .describe(
                '2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.'
              ),
            subdivisions: z.array(z.string()).min(0).max(100).optional(),
          })
        )
        .min(0)
        .max(250)
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the delivery region was created.')
        .optional()
        .nullable(),
    })
    .describe('Delivery region to add.'),
  options: z
    .object({
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe('Delivery profile revision.')
        .optional()
        .nullable(),
    })
    .optional(),
});
export const AddDeliveryRegionResponse = z.object({
  deliveryProfile: z
    .object({
      _id: z
        .string()
        .describe('Delivery profile ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Delivery profile name.')
        .min(1)
        .max(256)
        .optional()
        .nullable(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default delivery profile.\n\n> **Notes:**\n>\n> - The first delivery profile is automatically created and marked as default when the Wix Stores, Wix Bookings, Wix Events, or Wix Restaurants business solutions are installed on a site.\n> - Default status can't be changed or transferred to another profile.\n> - The default profile can't be deleted because every site requires one for core delivery functionality."
        )
        .optional()
        .nullable(),
      deliveryRegions: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Delivery region ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe(
                'Delivery region name. For example, `"Domestic"` or `"International"`.'
              )
              .min(1)
              .max(256)
              .optional()
              .nullable(),
            active: z
              .boolean()
              .describe(
                'Whether this delivery region is active and available during checkout.\n\nDefault: `true`'
              )
              .optional()
              .nullable(),
            deliveryCarriers: z
              .array(
                z.object({
                  appId: z
                    .string()
                    .describe(
                      'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  backupRate: z
                    .object({
                      title: z
                        .string()
                        .describe(
                          'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
                        )
                        .min(1)
                        .max(256)
                        .optional()
                        .nullable(),
                      amount: z
                        .string()
                        .describe('Backup rate amount.')
                        .optional()
                        .nullable(),
                      active: z
                        .boolean()
                        .describe(
                          'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
                        )
                        .optional(),
                    })
                    .describe(
                      "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
                    )
                    .optional(),
                  additionalCharges: z
                    .array(
                      z.object({
                        description: z
                          .string()
                          .describe(
                            'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                          )
                          .max(250)
                          .optional()
                          .nullable(),
                        amount: z
                          .string()
                          .describe(
                            'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                          )
                          .max(16)
                          .optional(),
                      })
                    )
                    .max(10)
                    .optional(),
                })
              )
              .min(0)
              .max(25)
              .optional(),
            destinations: z
              .array(
                z.object({
                  countryCode: z
                    .string()
                    .describe(
                      '2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.'
                    )
                    .optional(),
                  subdivisions: z.array(z.string()).min(0).max(100).optional(),
                })
              )
              .min(0)
              .max(250)
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the delivery region was created.')
              .optional()
              .nullable(),
          })
        )
        .min(0)
        .max(100)
        .optional(),
      createdBy: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              appId: z.never().optional(),
              userId: z.never().optional(),
            }),
            z.object({
              userId: z.never().optional(),
              appId: z
                .string()
                .describe(
                  'App ID, when the delivery profile was created by an external application or Wix service.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              appId: z.never().optional(),
              userId: z
                .string()
                .describe(
                  'Wix user ID, when the delivery profile was created by a Wix user using the dashboard or an API call.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe(
          'Information about who created the delivery profile.\n\nCurrently only for use with Wix Restaurants.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the delivery profile is updated. To prevent conflicting changes, the current revision must be passed when updating the delivery profile. Ignored when creating a delivery profile.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the delivery profile was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the delivery profile was last updated.')
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the delivery profile object. [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
    })
    .describe('Updated delivery profile with the new delivery region.')
    .optional(),
});
export const UpdateDeliveryRegionRequest = z.object({
  identifiers: z.object({
    deliveryProfileId: z
      .string()
      .describe('Delivery profile ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    deliveryRegionId: z
      .string()
      .describe('Delivery region ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
  }),
  deliveryRegion: z
    .object({
      _id: z
        .string()
        .describe('Delivery region ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe(
          'Delivery region name. For example, `"Domestic"` or `"International"`.'
        )
        .min(1)
        .max(256)
        .optional()
        .nullable(),
      active: z
        .boolean()
        .describe(
          'Whether this delivery region is active and available during checkout.\n\nDefault: `true`'
        )
        .optional()
        .nullable(),
      deliveryCarriers: z
        .array(
          z.object({
            appId: z
              .string()
              .describe(
                'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            backupRate: z
              .object({
                title: z
                  .string()
                  .describe(
                    'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
                  )
                  .min(1)
                  .max(256)
                  .optional()
                  .nullable(),
                amount: z
                  .string()
                  .describe('Backup rate amount.')
                  .optional()
                  .nullable(),
                active: z
                  .boolean()
                  .describe(
                    'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
                  )
                  .optional(),
              })
              .describe(
                "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
              )
              .optional(),
            additionalCharges: z
              .array(
                z.object({
                  description: z
                    .string()
                    .describe(
                      'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                    )
                    .max(250)
                    .optional()
                    .nullable(),
                  amount: z
                    .string()
                    .describe(
                      'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                    )
                    .max(16)
                    .optional(),
                })
              )
              .max(10)
              .optional(),
          })
        )
        .min(0)
        .max(25)
        .optional(),
      destinations: z
        .array(
          z.object({
            countryCode: z
              .string()
              .describe(
                '2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.'
              )
              .optional(),
            subdivisions: z.array(z.string()).min(0).max(100).optional(),
          })
        )
        .min(0)
        .max(250)
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the delivery region was created.')
        .optional()
        .nullable(),
    })
    .describe('Delivery region to update.'),
  options: z
    .object({
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe('Delivery profile revision.')
        .optional()
        .nullable(),
    })
    .optional(),
});
export const UpdateDeliveryRegionResponse = z.object({
  deliveryProfile: z
    .object({
      _id: z
        .string()
        .describe('Delivery profile ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Delivery profile name.')
        .min(1)
        .max(256)
        .optional()
        .nullable(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default delivery profile.\n\n> **Notes:**\n>\n> - The first delivery profile is automatically created and marked as default when the Wix Stores, Wix Bookings, Wix Events, or Wix Restaurants business solutions are installed on a site.\n> - Default status can't be changed or transferred to another profile.\n> - The default profile can't be deleted because every site requires one for core delivery functionality."
        )
        .optional()
        .nullable(),
      deliveryRegions: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Delivery region ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe(
                'Delivery region name. For example, `"Domestic"` or `"International"`.'
              )
              .min(1)
              .max(256)
              .optional()
              .nullable(),
            active: z
              .boolean()
              .describe(
                'Whether this delivery region is active and available during checkout.\n\nDefault: `true`'
              )
              .optional()
              .nullable(),
            deliveryCarriers: z
              .array(
                z.object({
                  appId: z
                    .string()
                    .describe(
                      'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  backupRate: z
                    .object({
                      title: z
                        .string()
                        .describe(
                          'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
                        )
                        .min(1)
                        .max(256)
                        .optional()
                        .nullable(),
                      amount: z
                        .string()
                        .describe('Backup rate amount.')
                        .optional()
                        .nullable(),
                      active: z
                        .boolean()
                        .describe(
                          'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
                        )
                        .optional(),
                    })
                    .describe(
                      "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
                    )
                    .optional(),
                  additionalCharges: z
                    .array(
                      z.object({
                        description: z
                          .string()
                          .describe(
                            'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                          )
                          .max(250)
                          .optional()
                          .nullable(),
                        amount: z
                          .string()
                          .describe(
                            'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                          )
                          .max(16)
                          .optional(),
                      })
                    )
                    .max(10)
                    .optional(),
                })
              )
              .min(0)
              .max(25)
              .optional(),
            destinations: z
              .array(
                z.object({
                  countryCode: z
                    .string()
                    .describe(
                      '2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.'
                    )
                    .optional(),
                  subdivisions: z.array(z.string()).min(0).max(100).optional(),
                })
              )
              .min(0)
              .max(250)
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the delivery region was created.')
              .optional()
              .nullable(),
          })
        )
        .min(0)
        .max(100)
        .optional(),
      createdBy: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              appId: z.never().optional(),
              userId: z.never().optional(),
            }),
            z.object({
              userId: z.never().optional(),
              appId: z
                .string()
                .describe(
                  'App ID, when the delivery profile was created by an external application or Wix service.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              appId: z.never().optional(),
              userId: z
                .string()
                .describe(
                  'Wix user ID, when the delivery profile was created by a Wix user using the dashboard or an API call.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe(
          'Information about who created the delivery profile.\n\nCurrently only for use with Wix Restaurants.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the delivery profile is updated. To prevent conflicting changes, the current revision must be passed when updating the delivery profile. Ignored when creating a delivery profile.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the delivery profile was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the delivery profile was last updated.')
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the delivery profile object. [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
    })
    .describe('Updated delivery profile with the updated delivery region.')
    .optional(),
});
export const GetDeliveryProfileByDeliveryRegionIdRequest = z.object({
  deliveryRegionId: z
    .string()
    .describe('Delivery region ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetDeliveryProfileByDeliveryRegionIdResponse = z.object({
  deliveryProfile: z
    .object({
      _id: z
        .string()
        .describe('Delivery profile ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Delivery profile name.')
        .min(1)
        .max(256)
        .optional()
        .nullable(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default delivery profile.\n\n> **Notes:**\n>\n> - The first delivery profile is automatically created and marked as default when the Wix Stores, Wix Bookings, Wix Events, or Wix Restaurants business solutions are installed on a site.\n> - Default status can't be changed or transferred to another profile.\n> - The default profile can't be deleted because every site requires one for core delivery functionality."
        )
        .optional()
        .nullable(),
      deliveryRegions: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Delivery region ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe(
                'Delivery region name. For example, `"Domestic"` or `"International"`.'
              )
              .min(1)
              .max(256)
              .optional()
              .nullable(),
            active: z
              .boolean()
              .describe(
                'Whether this delivery region is active and available during checkout.\n\nDefault: `true`'
              )
              .optional()
              .nullable(),
            deliveryCarriers: z
              .array(
                z.object({
                  appId: z
                    .string()
                    .describe(
                      'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  backupRate: z
                    .object({
                      title: z
                        .string()
                        .describe(
                          'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
                        )
                        .min(1)
                        .max(256)
                        .optional()
                        .nullable(),
                      amount: z
                        .string()
                        .describe('Backup rate amount.')
                        .optional()
                        .nullable(),
                      active: z
                        .boolean()
                        .describe(
                          'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
                        )
                        .optional(),
                    })
                    .describe(
                      "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
                    )
                    .optional(),
                  additionalCharges: z
                    .array(
                      z.object({
                        description: z
                          .string()
                          .describe(
                            'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                          )
                          .max(250)
                          .optional()
                          .nullable(),
                        amount: z
                          .string()
                          .describe(
                            'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                          )
                          .max(16)
                          .optional(),
                      })
                    )
                    .max(10)
                    .optional(),
                })
              )
              .min(0)
              .max(25)
              .optional(),
            destinations: z
              .array(
                z.object({
                  countryCode: z
                    .string()
                    .describe(
                      '2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.'
                    )
                    .optional(),
                  subdivisions: z.array(z.string()).min(0).max(100).optional(),
                })
              )
              .min(0)
              .max(250)
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the delivery region was created.')
              .optional()
              .nullable(),
          })
        )
        .min(0)
        .max(100)
        .optional(),
      createdBy: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              appId: z.never().optional(),
              userId: z.never().optional(),
            }),
            z.object({
              userId: z.never().optional(),
              appId: z
                .string()
                .describe(
                  'App ID, when the delivery profile was created by an external application or Wix service.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              appId: z.never().optional(),
              userId: z
                .string()
                .describe(
                  'Wix user ID, when the delivery profile was created by a Wix user using the dashboard or an API call.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe(
          'Information about who created the delivery profile.\n\nCurrently only for use with Wix Restaurants.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the delivery profile is updated. To prevent conflicting changes, the current revision must be passed when updating the delivery profile. Ignored when creating a delivery profile.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the delivery profile was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the delivery profile was last updated.')
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the delivery profile object. [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
    })
    .describe('Delivery profile containing the requested region.')
    .optional(),
});
export const RemoveDeliveryRegionRequest = z.object({
  identifiers: z.object({
    deliveryProfileId: z
      .string()
      .describe('Delivery profile ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    deliveryRegionId: z
      .string()
      .describe('Delivery region ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
  }),
  options: z
    .object({
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe('Delivery profile revision.')
        .optional()
        .nullable(),
    })
    .optional(),
});
export const RemoveDeliveryRegionResponse = z.object({
  deliveryProfile: z
    .object({
      _id: z
        .string()
        .describe('Delivery profile ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Delivery profile name.')
        .min(1)
        .max(256)
        .optional()
        .nullable(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default delivery profile.\n\n> **Notes:**\n>\n> - The first delivery profile is automatically created and marked as default when the Wix Stores, Wix Bookings, Wix Events, or Wix Restaurants business solutions are installed on a site.\n> - Default status can't be changed or transferred to another profile.\n> - The default profile can't be deleted because every site requires one for core delivery functionality."
        )
        .optional()
        .nullable(),
      deliveryRegions: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Delivery region ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe(
                'Delivery region name. For example, `"Domestic"` or `"International"`.'
              )
              .min(1)
              .max(256)
              .optional()
              .nullable(),
            active: z
              .boolean()
              .describe(
                'Whether this delivery region is active and available during checkout.\n\nDefault: `true`'
              )
              .optional()
              .nullable(),
            deliveryCarriers: z
              .array(
                z.object({
                  appId: z
                    .string()
                    .describe(
                      'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  backupRate: z
                    .object({
                      title: z
                        .string()
                        .describe(
                          'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
                        )
                        .min(1)
                        .max(256)
                        .optional()
                        .nullable(),
                      amount: z
                        .string()
                        .describe('Backup rate amount.')
                        .optional()
                        .nullable(),
                      active: z
                        .boolean()
                        .describe(
                          'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
                        )
                        .optional(),
                    })
                    .describe(
                      "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
                    )
                    .optional(),
                  additionalCharges: z
                    .array(
                      z.object({
                        description: z
                          .string()
                          .describe(
                            'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                          )
                          .max(250)
                          .optional()
                          .nullable(),
                        amount: z
                          .string()
                          .describe(
                            'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                          )
                          .max(16)
                          .optional(),
                      })
                    )
                    .max(10)
                    .optional(),
                })
              )
              .min(0)
              .max(25)
              .optional(),
            destinations: z
              .array(
                z.object({
                  countryCode: z
                    .string()
                    .describe(
                      '2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.'
                    )
                    .optional(),
                  subdivisions: z.array(z.string()).min(0).max(100).optional(),
                })
              )
              .min(0)
              .max(250)
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the delivery region was created.')
              .optional()
              .nullable(),
          })
        )
        .min(0)
        .max(100)
        .optional(),
      createdBy: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              appId: z.never().optional(),
              userId: z.never().optional(),
            }),
            z.object({
              userId: z.never().optional(),
              appId: z
                .string()
                .describe(
                  'App ID, when the delivery profile was created by an external application or Wix service.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              appId: z.never().optional(),
              userId: z
                .string()
                .describe(
                  'Wix user ID, when the delivery profile was created by a Wix user using the dashboard or an API call.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe(
          'Information about who created the delivery profile.\n\nCurrently only for use with Wix Restaurants.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the delivery profile is updated. To prevent conflicting changes, the current revision must be passed when updating the delivery profile. Ignored when creating a delivery profile.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the delivery profile was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the delivery profile was last updated.')
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the delivery profile object. [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
    })
    .describe('Updated delivery profile with the delivery region removed.')
    .optional(),
});
export const AddDeliveryCarrierRequest = z.object({
  deliveryRegionId: z
    .string()
    .describe('Delivery region ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    deliveryCarrier: z
      .object({
        appId: z
          .string()
          .describe(
            'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        backupRate: z
          .object({
            title: z
              .string()
              .describe(
                'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
              )
              .min(1)
              .max(256)
              .optional()
              .nullable(),
            amount: z.string().describe('Backup rate amount.'),
            active: z
              .boolean()
              .describe(
                'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
              )
              .optional(),
          })
          .describe(
            "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
          )
          .optional(),
        additionalCharges: z
          .array(
            z.object({
              description: z
                .string()
                .describe(
                  'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                )
                .max(250)
                .optional()
                .nullable(),
              amount: z
                .string()
                .describe(
                  'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                )
                .max(16)
                .optional(),
            })
          )
          .max(10)
          .optional(),
      })
      .describe('Delivery carrier to add.'),
  }),
});
export const AddDeliveryCarrierResponse = z.object({
  deliveryProfile: z
    .object({
      _id: z
        .string()
        .describe('Delivery profile ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Delivery profile name.')
        .min(1)
        .max(256)
        .optional()
        .nullable(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default delivery profile.\n\n> **Notes:**\n>\n> - The first delivery profile is automatically created and marked as default when the Wix Stores, Wix Bookings, Wix Events, or Wix Restaurants business solutions are installed on a site.\n> - Default status can't be changed or transferred to another profile.\n> - The default profile can't be deleted because every site requires one for core delivery functionality."
        )
        .optional()
        .nullable(),
      deliveryRegions: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Delivery region ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe(
                'Delivery region name. For example, `"Domestic"` or `"International"`.'
              )
              .min(1)
              .max(256)
              .optional()
              .nullable(),
            active: z
              .boolean()
              .describe(
                'Whether this delivery region is active and available during checkout.\n\nDefault: `true`'
              )
              .optional()
              .nullable(),
            deliveryCarriers: z
              .array(
                z.object({
                  appId: z
                    .string()
                    .describe(
                      'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  backupRate: z
                    .object({
                      title: z
                        .string()
                        .describe(
                          'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
                        )
                        .min(1)
                        .max(256)
                        .optional()
                        .nullable(),
                      amount: z
                        .string()
                        .describe('Backup rate amount.')
                        .optional()
                        .nullable(),
                      active: z
                        .boolean()
                        .describe(
                          'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
                        )
                        .optional(),
                    })
                    .describe(
                      "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
                    )
                    .optional(),
                  additionalCharges: z
                    .array(
                      z.object({
                        description: z
                          .string()
                          .describe(
                            'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                          )
                          .max(250)
                          .optional()
                          .nullable(),
                        amount: z
                          .string()
                          .describe(
                            'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                          )
                          .max(16)
                          .optional(),
                      })
                    )
                    .max(10)
                    .optional(),
                })
              )
              .min(0)
              .max(25)
              .optional(),
            destinations: z
              .array(
                z.object({
                  countryCode: z
                    .string()
                    .describe(
                      '2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.'
                    )
                    .optional(),
                  subdivisions: z.array(z.string()).min(0).max(100).optional(),
                })
              )
              .min(0)
              .max(250)
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the delivery region was created.')
              .optional()
              .nullable(),
          })
        )
        .min(0)
        .max(100)
        .optional(),
      createdBy: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              appId: z.never().optional(),
              userId: z.never().optional(),
            }),
            z.object({
              userId: z.never().optional(),
              appId: z
                .string()
                .describe(
                  'App ID, when the delivery profile was created by an external application or Wix service.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              appId: z.never().optional(),
              userId: z
                .string()
                .describe(
                  'Wix user ID, when the delivery profile was created by a Wix user using the dashboard or an API call.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe(
          'Information about who created the delivery profile.\n\nCurrently only for use with Wix Restaurants.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the delivery profile is updated. To prevent conflicting changes, the current revision must be passed when updating the delivery profile. Ignored when creating a delivery profile.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the delivery profile was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the delivery profile was last updated.')
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the delivery profile object. [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
    })
    .describe('Updated delivery profile.')
    .optional(),
});
export const RemoveDeliveryCarrierRequest = z.object({
  deliveryRegionId: z
    .string()
    .describe('Delivery region ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    appId: z
      .string()
      .describe('App ID of the delivery carrier to remove.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
  }),
});
export const RemoveDeliveryCarrierResponse = z.object({
  deliveryProfile: z
    .object({
      _id: z
        .string()
        .describe('Delivery profile ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Delivery profile name.')
        .min(1)
        .max(256)
        .optional()
        .nullable(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default delivery profile.\n\n> **Notes:**\n>\n> - The first delivery profile is automatically created and marked as default when the Wix Stores, Wix Bookings, Wix Events, or Wix Restaurants business solutions are installed on a site.\n> - Default status can't be changed or transferred to another profile.\n> - The default profile can't be deleted because every site requires one for core delivery functionality."
        )
        .optional()
        .nullable(),
      deliveryRegions: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Delivery region ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe(
                'Delivery region name. For example, `"Domestic"` or `"International"`.'
              )
              .min(1)
              .max(256)
              .optional()
              .nullable(),
            active: z
              .boolean()
              .describe(
                'Whether this delivery region is active and available during checkout.\n\nDefault: `true`'
              )
              .optional()
              .nullable(),
            deliveryCarriers: z
              .array(
                z.object({
                  appId: z
                    .string()
                    .describe(
                      'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  backupRate: z
                    .object({
                      title: z
                        .string()
                        .describe(
                          'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
                        )
                        .min(1)
                        .max(256)
                        .optional()
                        .nullable(),
                      amount: z
                        .string()
                        .describe('Backup rate amount.')
                        .optional()
                        .nullable(),
                      active: z
                        .boolean()
                        .describe(
                          'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
                        )
                        .optional(),
                    })
                    .describe(
                      "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
                    )
                    .optional(),
                  additionalCharges: z
                    .array(
                      z.object({
                        description: z
                          .string()
                          .describe(
                            'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                          )
                          .max(250)
                          .optional()
                          .nullable(),
                        amount: z
                          .string()
                          .describe(
                            'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                          )
                          .max(16)
                          .optional(),
                      })
                    )
                    .max(10)
                    .optional(),
                })
              )
              .min(0)
              .max(25)
              .optional(),
            destinations: z
              .array(
                z.object({
                  countryCode: z
                    .string()
                    .describe(
                      '2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.'
                    )
                    .optional(),
                  subdivisions: z.array(z.string()).min(0).max(100).optional(),
                })
              )
              .min(0)
              .max(250)
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the delivery region was created.')
              .optional()
              .nullable(),
          })
        )
        .min(0)
        .max(100)
        .optional(),
      createdBy: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              appId: z.never().optional(),
              userId: z.never().optional(),
            }),
            z.object({
              userId: z.never().optional(),
              appId: z
                .string()
                .describe(
                  'App ID, when the delivery profile was created by an external application or Wix service.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              appId: z.never().optional(),
              userId: z
                .string()
                .describe(
                  'Wix user ID, when the delivery profile was created by a Wix user using the dashboard or an API call.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe(
          'Information about who created the delivery profile.\n\nCurrently only for use with Wix Restaurants.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the delivery profile is updated. To prevent conflicting changes, the current revision must be passed when updating the delivery profile. Ignored when creating a delivery profile.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the delivery profile was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the delivery profile was last updated.')
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the delivery profile object. [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
    })
    .describe('Updated delivery profile.')
    .optional(),
});
export const UpdateDeliveryCarrierRequest = z.object({
  deliveryRegionId: z
    .string()
    .describe('Delivery region ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      deliveryCarrier: z
        .object({
          appId: z
            .string()
            .describe(
              'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
          backupRate: z
            .object({
              title: z
                .string()
                .describe(
                  'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
                )
                .min(1)
                .max(256)
                .optional()
                .nullable(),
              amount: z
                .string()
                .describe('Backup rate amount.')
                .optional()
                .nullable(),
              active: z
                .boolean()
                .describe(
                  'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
                )
                .optional(),
            })
            .describe(
              "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
            )
            .optional(),
          additionalCharges: z
            .array(
              z.object({
                description: z
                  .string()
                  .describe(
                    'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                  )
                  .max(250)
                  .optional()
                  .nullable(),
                amount: z
                  .string()
                  .describe(
                    'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                  )
                  .max(16)
                  .optional(),
              })
            )
            .max(10)
            .optional(),
        })
        .describe('Delivery carrier to update.')
        .optional(),
    })
    .optional(),
});
export const UpdateDeliveryCarrierResponse = z.object({
  deliveryProfile: z
    .object({
      _id: z
        .string()
        .describe('Delivery profile ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Delivery profile name.')
        .min(1)
        .max(256)
        .optional()
        .nullable(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default delivery profile.\n\n> **Notes:**\n>\n> - The first delivery profile is automatically created and marked as default when the Wix Stores, Wix Bookings, Wix Events, or Wix Restaurants business solutions are installed on a site.\n> - Default status can't be changed or transferred to another profile.\n> - The default profile can't be deleted because every site requires one for core delivery functionality."
        )
        .optional()
        .nullable(),
      deliveryRegions: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Delivery region ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe(
                'Delivery region name. For example, `"Domestic"` or `"International"`.'
              )
              .min(1)
              .max(256)
              .optional()
              .nullable(),
            active: z
              .boolean()
              .describe(
                'Whether this delivery region is active and available during checkout.\n\nDefault: `true`'
              )
              .optional()
              .nullable(),
            deliveryCarriers: z
              .array(
                z.object({
                  appId: z
                    .string()
                    .describe(
                      'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  backupRate: z
                    .object({
                      title: z
                        .string()
                        .describe(
                          'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
                        )
                        .min(1)
                        .max(256)
                        .optional()
                        .nullable(),
                      amount: z
                        .string()
                        .describe('Backup rate amount.')
                        .optional()
                        .nullable(),
                      active: z
                        .boolean()
                        .describe(
                          'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
                        )
                        .optional(),
                    })
                    .describe(
                      "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
                    )
                    .optional(),
                  additionalCharges: z
                    .array(
                      z.object({
                        description: z
                          .string()
                          .describe(
                            'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                          )
                          .max(250)
                          .optional()
                          .nullable(),
                        amount: z
                          .string()
                          .describe(
                            'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                          )
                          .max(16)
                          .optional(),
                      })
                    )
                    .max(10)
                    .optional(),
                })
              )
              .min(0)
              .max(25)
              .optional(),
            destinations: z
              .array(
                z.object({
                  countryCode: z
                    .string()
                    .describe(
                      '2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.'
                    )
                    .optional(),
                  subdivisions: z.array(z.string()).min(0).max(100).optional(),
                })
              )
              .min(0)
              .max(250)
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the delivery region was created.')
              .optional()
              .nullable(),
          })
        )
        .min(0)
        .max(100)
        .optional(),
      createdBy: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              appId: z.never().optional(),
              userId: z.never().optional(),
            }),
            z.object({
              userId: z.never().optional(),
              appId: z
                .string()
                .describe(
                  'App ID, when the delivery profile was created by an external application or Wix service.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              appId: z.never().optional(),
              userId: z
                .string()
                .describe(
                  'Wix user ID, when the delivery profile was created by a Wix user using the dashboard or an API call.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe(
          'Information about who created the delivery profile.\n\nCurrently only for use with Wix Restaurants.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the delivery profile is updated. To prevent conflicting changes, the current revision must be passed when updating the delivery profile. Ignored when creating a delivery profile.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the delivery profile was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the delivery profile was last updated.')
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the delivery profile object. [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
    })
    .describe('Updated delivery profile with the updated delivery carrier.')
    .optional(),
});
export const ListInstalledDeliveryCarriersRequest = z.object({});
export const ListInstalledDeliveryCarriersResponse = z.object({
  installedDeliveryCarriers: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Carrier app ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        displayName: z
          .string()
          .describe('Carrier display name.')
          .max(256)
          .optional(),
        description: z
          .string()
          .describe('Carrier description.')
          .max(200)
          .optional()
          .nullable(),
        learnMoreUrl: z
          .string()
          .describe('URL to learn more about the carrier.')
          .max(200)
          .optional()
          .nullable(),
        dashboardUrl: z
          .string()
          .describe("URL to the carrier's dashboard.")
          .min(1)
          .max(2048)
          .url()
          .optional()
          .nullable(),
        fallbackDefinitionMandatory: z
          .boolean()
          .describe('Whether a backup rate is required for this carrier.')
          .optional()
          .nullable(),
        thumbnailUrl: z
          .string()
          .describe("URL for the carrier's thumbnail image.")
          .url()
          .optional()
          .nullable(),
        toggleGetCarrierSettingsEnabled: z
          .boolean()
          .describe('Whether the carrier supports retrieving carrier settings.')
          .optional(),
      })
    )
    .max(100)
    .optional(),
});
export const GetDeliveryDestinationPropertiesRequest = z.object({});
export const GetDeliveryDestinationPropertiesResponse = z.object({
  restOfWorld: z
    .object({
      postalCodeRequired: z
        .boolean()
        .describe(
          'Whether a postal code is required for delivery to this destination.'
        )
        .optional(),
      pickupOnly: z
        .boolean()
        .describe('Whether only pickup is available at this destination.')
        .optional(),
    })
    .describe('Delivery destination properties for Rest of World.')
    .optional(),
  countries: z
    .array(
      z.object({
        country: z
          .string()
          .describe(
            '2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.'
          )
          .optional(),
        deliveryDestinationProperties: z
          .object({
            postalCodeRequired: z
              .boolean()
              .describe(
                'Whether a postal code is required for delivery to this destination.'
              )
              .optional(),
            pickupOnly: z
              .boolean()
              .describe('Whether only pickup is available at this destination.')
              .optional(),
          })
          .describe('Delivery destination properties for the country.')
          .optional(),
        subdivisions: z
          .array(
            z.object({
              subdivision: z
                .string()
                .describe(
                  'Code for a subdivision (such as state, prefecture, or province) in [ISO 3166-2](https://www.iso.org/standard/72483.html) format.'
                )
                .min(1)
                .max(5)
                .optional(),
              deliveryDestinationProperties: z
                .object({
                  postalCodeRequired: z
                    .boolean()
                    .describe(
                      'Whether a postal code is required for delivery to this destination.'
                    )
                    .optional(),
                  pickupOnly: z
                    .boolean()
                    .describe(
                      'Whether only pickup is available at this destination.'
                    )
                    .optional(),
                })
                .describe(
                  'Delivery destination properties for the subdivision.'
                )
                .optional(),
            })
          )
          .max(100)
          .optional(),
      })
    )
    .max(195)
    .optional(),
});
export const ListDeliveryCarriersRequest = z.object({
  deliveryProfileId: z
    .string()
    .describe('Delivery profile ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({ appIds: z.array(z.string()).min(1).max(25).optional() })
    .optional(),
});
export const ListDeliveryCarriersResponse = z.object({
  results: z
    .array(
      z.object({
        deliveryCarrierMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe(
            'Metadata for the result, including success or error information.'
          )
          .optional(),
        deliveryCarrierDetails: z
          .object({
            _id: z
              .string()
              .describe('Carrier app ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            displayName: z
              .string()
              .describe('Carrier display name.')
              .max(256)
              .optional(),
            description: z
              .string()
              .describe('Carrier description.')
              .max(200)
              .optional()
              .nullable(),
            learnMoreUrl: z
              .string()
              .describe('URL to learn more about the carrier.')
              .max(200)
              .optional()
              .nullable(),
            dashboardUrl: z
              .string()
              .describe("URL to the carrier's dashboard.")
              .min(1)
              .max(2048)
              .url()
              .optional()
              .nullable(),
            fallbackDefinitionMandatory: z
              .boolean()
              .describe('Whether a backup rate is required for this carrier.')
              .optional()
              .nullable(),
            thumbnailUrl: z
              .string()
              .describe("URL for the carrier's thumbnail image.")
              .url()
              .optional()
              .nullable(),
            toggleGetCarrierSettingsEnabled: z
              .boolean()
              .describe(
                'Whether the carrier supports retrieving carrier settings.'
              )
              .optional(),
            toggleUpdateCarrierActiveStatusEnabled: z
              .boolean()
              .describe(
                'Whether the carrier supports updating carrier active status.'
              )
              .optional(),
          })
          .describe('Delivery carrier details.')
          .optional(),
        deliveryCarrierRegionalSettings: z
          .array(
            z.object({
              deliveryRegionId: z
                .string()
                .describe('Delivery region ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              dashboardTables: z
                .array(
                  z.object({
                    title: z
                      .string()
                      .describe('Table title.')
                      .max(256)
                      .optional(),
                    columns: z
                      .array(
                        z.object({
                          key: z
                            .string()
                            .describe(
                              'Column key, used to retrieve data from rows.'
                            )
                            .max(256)
                            .optional(),
                          name: z
                            .string()
                            .describe('Column display name.')
                            .max(256)
                            .optional(),
                        })
                      )
                      .max(200)
                      .optional(),
                    rows: z
                      .array(
                        z.object({
                          key: z
                            .string()
                            .describe(
                              'Unique row identifier, used to identify the row for editing.'
                            )
                            .max(256)
                            .optional(),
                          data: z
                            .record(z.string(), z.any())
                            .describe(
                              'Row data as a JSON object. Each key corresponds to a column key.'
                            )
                            .optional()
                            .nullable(),
                          active: z
                            .boolean()
                            .describe(
                              "Whether this row's configuration is active."
                            )
                            .optional(),
                        })
                      )
                      .max(25)
                      .optional(),
                  })
                )
                .min(1)
                .max(5)
                .optional(),
            })
          )
          .min(1)
          .max(5)
          .optional(),
      })
    )
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const SetDeliveryCarrierActiveStatusRequest = z.object({
  carrierAppId: z
    .string()
    .describe('Carrier app ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    rowId: z
      .string()
      .describe(
        "Row identifier in the carrier's dashboard settings table that identifies the specific shipping configuration to update.\n\nPass the `row.key` field from the response of [List Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-delivery-carriers)."
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    active: z
      .boolean()
      .describe('Whether the configuration defined in the row is active.'),
  }),
});
export const SetDeliveryCarrierActiveStatusResponse = z.object({});
export const UpdateExtendedFieldsRequest = z.object({
  _id: z.string().describe('ID of the entity to update.'),
  namespace: z
    .string()
    .describe(
      'Identifier for the app whose extended fields are being updated.'
    ),
  options: z.object({
    namespaceData: z
      .record(z.string(), z.any())
      .describe(
        'Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured.'
      ),
  }),
});
export const UpdateExtendedFieldsResponse = z.object({
  deliveryProfile: z
    .object({
      _id: z
        .string()
        .describe('Delivery profile ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Delivery profile name.')
        .min(1)
        .max(256)
        .optional()
        .nullable(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default delivery profile.\n\n> **Notes:**\n>\n> - The first delivery profile is automatically created and marked as default when the Wix Stores, Wix Bookings, Wix Events, or Wix Restaurants business solutions are installed on a site.\n> - Default status can't be changed or transferred to another profile.\n> - The default profile can't be deleted because every site requires one for core delivery functionality."
        )
        .optional()
        .nullable(),
      deliveryRegions: z
        .array(
          z.object({
            _id: z
              .string()
              .describe('Delivery region ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe(
                'Delivery region name. For example, `"Domestic"` or `"International"`.'
              )
              .min(1)
              .max(256)
              .optional()
              .nullable(),
            active: z
              .boolean()
              .describe(
                'Whether this delivery region is active and available during checkout.\n\nDefault: `true`'
              )
              .optional()
              .nullable(),
            deliveryCarriers: z
              .array(
                z.object({
                  appId: z
                    .string()
                    .describe(
                      'Carrier app ID.\n\nGet app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  backupRate: z
                    .object({
                      title: z
                        .string()
                        .describe(
                          'Title displayed for the backup rate. For example, `"Standard Shipping"`.'
                        )
                        .min(1)
                        .max(256)
                        .optional()
                        .nullable(),
                      amount: z
                        .string()
                        .describe('Backup rate amount.')
                        .optional()
                        .nullable(),
                      active: z
                        .boolean()
                        .describe(
                          'Whether the backup rate is active.\n\nThe backup rate is used in the following situations:\n+ When `active` is `true`.\n+ When the carrier does not return a cost for any reason.\n\nIf active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.\n\nDefault: `false`'
                        )
                        .optional(),
                    })
                    .describe(
                      "Backup rate to use when the carrier doesn't return a rate for a specific shipping option.\n\nWhen `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers."
                    )
                    .optional(),
                  additionalCharges: z
                    .array(
                      z.object({
                        description: z
                          .string()
                          .describe(
                            'Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.'
                          )
                          .max(250)
                          .optional()
                          .nullable(),
                        amount: z
                          .string()
                          .describe(
                            'Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.'
                          )
                          .max(16)
                          .optional(),
                      })
                    )
                    .max(10)
                    .optional(),
                })
              )
              .min(0)
              .max(25)
              .optional(),
            destinations: z
              .array(
                z.object({
                  countryCode: z
                    .string()
                    .describe(
                      '2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.'
                    )
                    .optional(),
                  subdivisions: z.array(z.string()).min(0).max(100).optional(),
                })
              )
              .min(0)
              .max(250)
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the delivery region was created.')
              .optional()
              .nullable(),
          })
        )
        .min(0)
        .max(100)
        .optional(),
      createdBy: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              appId: z.never().optional(),
              userId: z.never().optional(),
            }),
            z.object({
              userId: z.never().optional(),
              appId: z
                .string()
                .describe(
                  'App ID, when the delivery profile was created by an external application or Wix service.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              appId: z.never().optional(),
              userId: z
                .string()
                .describe(
                  'Wix user ID, when the delivery profile was created by a Wix user using the dashboard or an API call.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe(
          'Information about who created the delivery profile.\n\nCurrently only for use with Wix Restaurants.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the delivery profile is updated. To prevent conflicting changes, the current revision must be passed when updating the delivery profile. Ignored when creating a delivery profile.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the delivery profile was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the delivery profile was last updated.')
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the delivery profile object. [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
    })
    .describe('Updated delivery profile.')
    .optional(),
});
